import React, { useState, useEffect, useCallback } from "react";
import ProfileNav from "../../components/ProfileNav";
import toast from "react-hot-toast";
import { getOrganisationRoles, createRole, getOrgAdminPermissions, getAvailablePermissions } from "../../services/userservice";

const fallbackPermissions = [
  { id: 1, name: "Create Payment Link", description: "Allows creating payment links" },
  { id: 2, name: "Update User", description: "Allows updating users" },
  { id: 3, name: "List Roles", description: "Allows listing roles" },
  { id: 4, name: "Create Role", description: "Allows creating new roles" },
  { id: 5, name: "Get Permission List", description: "Allows retrieving permission lists" },
  { id: 6, name: "Get Credentials", description: "Allows reading api credentials" },
  { id: 7, name: "Add Settlement Account", description: "Allows adding settlement accounts" },
  { id: 8, name: "View Dashboard", description: "Allows viewing dashboard" },
  { id: 9, name: "List Users", description: "Allows listing organization users" },
  { id: 10, name: "Invite New Member", description: "Allows inviting new staff members" },
  { id: 11, name: "Disable User", description: "Allows disabling user accounts" },
  { id: 12, name: "Enable User", description: "Allows enabling user accounts" },
  { id: 13, name: "List Transactions", description: "Allows viewing transactions" },
  { id: 14, name: "View Audit Trails", description: "Allows viewing activity audit logs" },
  { id: 15, name: "Create User", description: "Allows creating new users" }
];

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);


  const fetchRolesData = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const res = await getOrganisationRoles();
      if (res?.data) {
        setRoles(res.data);
      } else if (Array.isArray(res)) {
        setRoles(res);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error(error?.response?.data?.message || "Failed to load roles.");
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const fetchPermissionsData = useCallback(async () => {
    setLoadingPermissions(true);
    try {
      let res;
      try {
        res = await getOrgAdminPermissions();
      } catch (err) {
        console.warn("Failed fetching from org-admin/permissions, trying permissions/available:", err);
        res = await getAvailablePermissions();
      }
      const perms = res?.data || res;
      if (Array.isArray(perms) && perms.length > 0) {
        const normalized = perms.map((item, idx) => {
          if (typeof item === 'string') {
            return { id: item, name: item, description: "" };
          }
          if (item && typeof item === 'object') {
            return {
              id: item.id !== undefined && item.id !== null ? item.id : item.name,
              name: item.name || `Permission ${idx}`,
              description: item.description || ""
            };
          }
          return null;
        }).filter(Boolean);
        setAvailablePermissions(normalized);
      } else {
        setAvailablePermissions(fallbackPermissions);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setAvailablePermissions(fallbackPermissions);
    } finally {
      setLoadingPermissions(false);
    }
  }, []);

  useEffect(() => {
    fetchRolesData();
    fetchPermissionsData();
  }, [fetchRolesData, fetchPermissionsData]);

  const handleCheckboxChange = (id) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedPermissionIds.length === availablePermissions.length) {
      setSelectedPermissionIds([]);
    } else {
      setSelectedPermissionIds(availablePermissions.map((p) => p.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toast.error("Please enter a role name.");
      return;
    }
    if (selectedPermissionIds.length === 0) {
      toast.error("Please select at least one permission.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: roleName,
        description: roleDescription,
        permissionIds: selectedPermissionIds
      };
      await createRole(payload);
      toast.success("Role created successfully!");
      // Reset form
      setRoleName("");
      setRoleDescription("");
      setSelectedPermissionIds([]);
      // Refresh list
      fetchRolesData();
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error(error?.response?.data?.message || "Failed to create role.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-[#334155] p-5">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-6">
        {/* Header Section */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-[#334155] mt-0">Roles & Permissions</h1>
            <p className="text-[#64748b] mt-2">Manage user roles and assign access permissions for the organization.</p>
          </div>
          <div>
            <ProfileNav />
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Create Role Form */}
          <div className="w-full lg:w-5/12 bg-white border border-[#e2e8f0] rounded-lg shadow-sm p-6 flex flex-col h-fit">
            <h2 className="text-lg font-bold text-[#334155] mb-4 border-b pb-3">Create New Role</h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="roleName" className="text-sm font-semibold text-[#475569]">
                  Role Name
                </label>
                <input
                  type="text"
                  id="roleName"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Finance Auditor"
                  className="h-[45px] border border-[#cbd5e1] rounded-md px-3 text-sm focus:outline-none focus:border-primary transition-colors"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="roleDescription" className="text-sm font-semibold text-[#475569]">
                  Description
                </label>
                <textarea
                  id="roleDescription"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  placeholder="Describe the responsibilities of this role..."
                  rows="3"
                  className="border border-[#cbd5e1] rounded-md p-3 text-sm focus:outline-none focus:border-primary transition-colors resize-none"
                />
              </div>

              {/* Permissions list */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-semibold text-[#475569]">
                    Assign Permissions ({selectedPermissionIds.length} selected)
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-primary font-semibold hover:underline"
                  >
                    {selectedPermissionIds.length === availablePermissions.length ? "Deselect All" : "Select All"}
                  </button>
                </div>

                {loadingPermissions ? (
                  <div className="text-center py-6 text-sm text-[#64748b]">Loading permissions...</div>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto border border-[#e2e8f0] rounded-md p-3 flex flex-col gap-2 bg-[#f8fafc]">
                    {availablePermissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-start gap-3 p-2 rounded-md hover:bg-white border border-transparent hover:border-[#e2e8f0] cursor-pointer transition-all"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissionIds.includes(permission.id)}
                          onChange={() => handleCheckboxChange(permission.id)}
                          className="mt-1 accent-primary w-4 h-4 rounded border-gray-300 focus:ring-primary"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-[#334155]">{permission.description}</span>
                          <span className="text-xs text-[#64748b]">{permission.name || "No description provided."}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-4 h-11 bg-primary text-white text-sm font-semibold rounded-md hover:opacity-90 transition-opacity flex items-center justify-center disabled:opacity-50"
              >
                {submitting ? "Saving Role..." : "Save User Role"}
              </button>
            </form>
          </div>

          {/* Existing Roles List */}
          <div className="w-full lg:w-7/12 bg-white border border-[#e2e8f0] rounded-lg shadow-sm p-6 flex flex-col">
            <h2 className="text-lg font-bold text-[#334155] mb-4 border-b pb-3">Existing Roles</h2>

            {loadingRoles ? (
              <div className="text-center py-12 text-[#64748b]">Loading roles list...</div>
            ) : roles.length === 0 ? (
              <div className="text-center py-12 text-[#64748b] bg-[#f8fafc] rounded-md border border-dashed border-[#cbd5e1]">
                No roles defined yet. Create a new one on the left.
              </div>
            ) : (
              <div className="flex flex-col gap-4 max-h-[75vh] overflow-y-auto pr-2">
                {roles.map((role) => (
                  <div
                    key={role.id || role.name}
                    className="border border-[#e2e8f0] rounded-lg p-4 hover:shadow-md transition-shadow bg-[#f8fafc]"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-base font-bold text-[#334155]">{role.description}</h3>
                        <p className="text-sm text-[#64748b] mt-1">{role.name || "No description provided."}</p>
                      </div>
                    </div>

                    {/* Associated permissions badge wraps */}
                    <div className="mt-3">
                      <span className="text-xs font-semibold text-[#475569] block mb-2">Permissions:</span>
                      <div className="flex flex-wrap gap-1.5">
                        {role.permissions && role.permissions.length > 0 ? (
                          role.permissions.map((p) => {
                            const name = typeof p === 'string' ? p : (p?.name || '');
                            const key = typeof p === 'string' ? p : (p?.id || p?.name || '');
                            return (
                              <span
                                key={key}
                                className="px-2.5 py-1 bg-white border border-[#e2e8f0] rounded-full text-xs font-medium text-[#475569]"
                              >
                                {name}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-[#94a3b8] italic">No permissions assigned.</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
